import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { caseTypesApi, extractList } from '../../api/facades';

const CaseTypesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<'Yes' | 'No' | ''>('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: parentsRes } = useQuery({
    queryKey: ['case-types-parents'],
    queryFn: () => caseTypesApi.list({}),
  });
  const parents = extractList(parentsRes).filter((p: any) => !p.parent_id);

  const { data: res, isLoading } = useQuery({
    queryKey: ['case-types', search, isActive, parentId],
    queryFn: () =>
      caseTypesApi.list({
        search: search || undefined,
        isActive: isActive || undefined,
        parentId: typeof parentId === 'number' ? parentId : undefined,
      }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => caseTypesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-types'] });
      setDeleteId(null);
      toast.success('Case type deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'case_type_name', label: 'Name' },
    {
      key: 'parent',
      label: 'Parent',
      render: (r: any) => r.parent?.case_type_name ?? (r.parent_id ? `#${r.parent_id}` : '—'),
    },
    {
      key: 'is_active',
      label: 'Active',
      render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No'),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Case Types"
        subtitle="Manage case type categories."
        primaryAction={{ label: 'New Case Type', to: '/dashboard/settings/case-types/new' }}
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
        <select
          value={parentId === '' ? '' : parentId}
          onChange={(e) => setParentId(e.target.value === '' ? '' : Number(e.target.value))}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
        >
          <option value="">All parents</option>
          {parents.map((p: any) => (
            <option key={p.id} value={p.id}>{p.case_type_name}</option>
          ))}
        </select>
        <select
          value={isActive}
          onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No' | '')}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
        >
          <option value="">All</option>
          <option value="Yes">Active</option>
          <option value="No">Inactive</option>
        </select>
        <input
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48"
        />
      </div>
      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        emptyMessage="No case types."
        onRowClick={(id) => navigate(`/dashboard/settings/case-types/${id}`)}
        onEdit={(id) => navigate(`/dashboard/settings/case-types/${id}/edit`)}
        onDelete={(id) => setDeleteId(Number(id))}
      />
      </div>
      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)}
        title="Delete case type"
        message="Are you sure? This cannot be undone."
        loading={deleteMu.isPending}
      />
    </div>
  );
};

export default CaseTypesListPage;
